/* global JSX */
import { Box, Grid, Typography } from "@mui/material";
import * as React from "react";
import { EventListStateCol } from "ui/Scripts/source/components/event/eventListStateCol";
import { ToolButtonDropdown } from "ui/Scripts/source/components/framework/Button/ToolButtonDropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { IdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ApplicationPermissionType, ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { ModifyUser } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/user/modifyUser";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { ISavedPrint, SavedPrint } from "../../../models/common/savedPrint";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { IEventListItem } from "../../../models/event/eventListItem";
import * as eventService from "../../../models/services/eventService";
import { ISessionState } from "../../../models/session/sessionState";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { EditToolButton } from "../../framework/Button/EditToolButton";
import { PreviewToggleButton } from "../../framework/Button/PreviewToggleButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { AppPrintIcon } from "../../framework/Icon/AppPrintIcon";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { PrintOptionsDialog } from "../../framework/PrintOptionsDialog/PrintOptionsDialog";
import { EventEditDialog } from "../EventEditDialog";
import { EventEditNewEventDialog } from "../EventEditNewEventDialog";
import { EventListDetailsPanel } from "./EventListDetailsPanel";
import { cloneEventGeneralFilter, getEventGeneralFilterChange, getEventGeneralFilterFromFilterState, IEventGeneralFilter, IEventPageFilterState } from "../EventPage/EventPageUtils";
import { EventListHandlersCol } from "./EventListHandlersCol";
import { EventListDetailsHandlersField } from "./EventListDetailsHandlersField";

export interface IEventListProp extends IEventPageFilterState {
    sessionState: ISessionState;
    savedFilter: IListFilterItem;
    companyId: string;
    disableGlobalLoading?: boolean;
    addRecord: boolean;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    titleSuffix?: JSX.Element;
    reportTabs: React.ReactNode;
    userPrints: ISavedPrint[];
    printableFields: IIdTitleNode[];
    showDocumentPreview?: boolean;
    urgencyClasses: IdColorTitle[];
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onUserPrintsModified: (userPrints: ISavedPrint[]) => void;
    onOpenFiltersDialog: () => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
}

interface IEventListState extends IListUsingComponentState<IEventListItem>, IEventGeneralFilter {
    userPrints: ISavedPrint[];

    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    filter: string;

    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];

    // Details
    detailsItem: IEventListItem;

    // New Dialog
    showNewEventDialog: boolean;

    // Prints
    showEventPrintDialog: boolean;
    eventPrintDialogPrint: ISavedPrint;
    eventPrintDialogReservedNames: string[];
    eventPrintDialogPrintableFields: IIdTitleNode[];
}

export class EventList extends React.Component<IEventListProp, IEventListState> {
    static defaultPrintId = "00000000-0000-0000-0000-000000000000";
    static customPrintId = "00000000-0000-0000-0000-000000000001";
    sendReadReceipts: string[] = [];

    getUserPrints = (userPrints: ISavedPrint[]): ISavedPrint[] => {
        let result = userPrints.filter(i => i.defaultPrint);
        const customPrints = userPrints.filter(i => !i.defaultPrint);
        if (customPrints.length) {
            result.push(SavedPrint.createSpacer());
            result = result.concat(customPrints);
        }
        result.push(SavedPrint.createSpacer());
        const addItem = new SavedPrint();
        addItem.title = Translations.AddPrint;
        result.push(addItem);
        return result;
    };

    sortHandlers = (items: IEventListItem[]): IEventListItem[] => {
        for (const item of items) {
            item.handlers = ModifyUser.sortModifyUsers(item.handlers, { currentUserCode: this.props.sessionState.code });
        }
        return items;
    };

    constructor(props: IEventListProp) {
        super(props);
        const generalFilterValues = getEventGeneralFilterFromFilterState(props);
        this.state = {
            ...generalFilterValues,
            userPrints: this.getUserPrints(props.userPrints),
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            // Details
            detailsItem: null,
            // New Dialog
            showNewEventDialog: props.addRecord,
            // Dialog
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            // Prints
            showEventPrintDialog: false,
            eventPrintDialogPrint: null,
            eventPrintDialogReservedNames: [],
            eventPrintDialogPrintableFields: []
        };
    }

    setReadReceipt = (detailsItem: IEventListItem) => {
        const props = this.props;
        if (!detailsItem || detailsItem.isRead || !detailsItem.published) return;
        const userIsCreator = detailsItem.createUser.code === props.sessionState.code;
        if (userIsCreator || this.sendReadReceipts.indexOf(detailsItem.id) > -1) return;
        const result = AppUtils.callService(() => eventService.sendEventReadReceipt(props.companyId, detailsItem.id), false, true);
        if (!result) return;
        this.sendReadReceipts.push(detailsItem.id);
        const items = this.state.items.slice(0);
        const item = items.find(i => i.id === detailsItem.id);
        if (!item) return;
        item.isRead = true;
        this.setState({
            items
        });
    };

    searchItems = async (pageSize: number, page: number, filter: string, genralFilter: IEventGeneralFilter, sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => eventService.getEventListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter,
            genralFilter.startDate, genralFilter.endDate, genralFilter.unitIds, genralFilter.states, genralFilter.urgencyClassIds, genralFilter.eventTypeIds, genralFilter.eventTypeBIds,
            genralFilter.eventTypeCIds, genralFilter.partnerCompanyIds, genralFilter.createUserCodes, genralFilter.handlerUserCodes, genralFilter.observers, genralFilter.dynamicTimeFilterId,
            sortColumn, sortOrderIsAsc), false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, this.sortHandlers(result.items), resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
            ...genralFilter
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
        this.setReadReceipt(detailsItem);
    };

    refreshList = async (setDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, cloneEventGeneralFilter(state), state.sortColumn, state.sortOrderIsAsc, false, true, false, setDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, cloneEventGeneralFilter(state), state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    componentDidUpdate(prevProps: IEventListProp, prevState: IEventListState) {
        const props = this.props;
        const state = this.state;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const filterStateChange = getEventGeneralFilterChange(prevProps, props);
        const prevSortColumn = prevProps.sortColumn;
        const sortColumn = props.sortColumn;
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
        const sortOrderIsAsc = props.sortOrderIsAsc;
        if (JSON.stringify(prevProps.userPrints) !== JSON.stringify(props.userPrints)) {
            this.setState({
                userPrints: this.getUserPrints(props.userPrints)
            });
        }
        if (dataId === prevDataId && companyId === prevCompanyId &&
            filterStateChange.notChanged &&
            prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) {
            return;
        }
        this.searchItems(state.pageSize, 1, state.filter, filterStateChange, sortColumn, sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, cloneEventGeneralFilter(state), state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, cloneEventGeneralFilter(state), sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        const detailsItem = this.state.items.find(i => i.id === selectedId);
        this.setState({
            selectedId,
            checkedIds,
            detailsItem
        });
        this.setReadReceipt(detailsItem);
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    // #region Edit dialog
    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleEventDialogOk = () => {
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    handleEventDialogCancel = () => {
        this.setState({
            showEditDialog: false
        });
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, cloneEventGeneralFilter(state), state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };
    // #endregion Edit dialog

    // #region Add dialog
    handleAdd = () => {
        this.setState({
            showNewEventDialog: true
        });
    };

    handleEditNewRecordDialogOk = async (eventId: string) => {
        this.setState({
            selectedId: eventId,
            showNewEventDialog: false
        });
        await this.refreshList();
        this.props.onLoadPageData();
        this.setState(List.getShowEditDialogStateChange(this.state, eventId));
    };

    handleEditNewRecordDialogCancel = () => {
        this.setState({
            showNewEventDialog: false
        });
    };
    // #endregion Add dialog

    handleApprove = async (item: IEventListItem) => {
        if (!item) return;
        if (await AppUtils.showConfirmationDialog(!item.immediateAction ? Translations.ApproveEventHandlerConfirmation : Translations.ApproveEventHandlerConfirmation2, false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => eventService.approveEvent(item.companyId, item.createCompanyId, item.id));
            if (!result) return;
            this.refreshList(false);
        }
    };

    handleShowPrintProperties = (e: any, id: string) => {
        const props = this.props;
        e.stopPropagation();
        e.preventDefault();
        const print = props.userPrints.find(i => i.id === id);
        if (!print) return;
        this.setState({
            showEventPrintDialog: true,
            eventPrintDialogPrint: print,
            eventPrintDialogReservedNames: props.userPrints.map(i => i.title).filter(i => i !== print.title),
            eventPrintDialogPrintableFields: id !== EventList.customPrintId
                ? props.printableFields
                : IdTitleNode.removeIds(props.printableFields, ["EventTypeA", "EventTypeB", "EventTypeC", "DirectCosts", "IndirectCosts"])
        });
    };

    handleAddPrint = () => {
        const props = this.props;
        const print = props.userPrints.find(i => i.id === EventList.defaultPrintId);
        if (!print) return;
        const newPrint = new SavedPrint(print);
        newPrint.defaultPrint = false;
        newPrint.title = Translations.NewPrint;
        let index = 2;
        while (props.userPrints.find(i => i.title === newPrint.title)) {
            newPrint.title = Translations.NewPrint + " " + index.toString(10);
            index++;
        }
        newPrint.id = newPrint.title;
        this.setState({
            showEventPrintDialog: true,
            eventPrintDialogPrint: newPrint,
            eventPrintDialogReservedNames: props.userPrints.map(i => i.title).filter(i => i !== newPrint.title),
            eventPrintDialogPrintableFields: props.printableFields
        });
    };

    handlePrintDialogSaved = (print: ISavedPrint) => {
        const props = this.props;
        const state = this.state;
        if (!print || !state.eventPrintDialogPrint) return;
        this.setState({
            showEventPrintDialog: false
        });
        const userPrints = props.userPrints.filter(i => i.id !== state.eventPrintDialogPrint.id);
        userPrints.push(print);
        props.onUserPrintsModified(SavedPrint.sortSavedPrints(userPrints));
    };

    handlePrintDialogRemoved = (printId: string) => {
        const props = this.props;
        if (!printId) return;
        this.setState({
            showEventPrintDialog: false
        });
        props.onUserPrintsModified(props.userPrints.filter(i => i.id !== printId));
    };

    hanlePrintDialogCancel = () => {
        this.setState({
            showEventPrintDialog: false
        });
    };

    handlePrint = async (printId: string) => {
        const props = this.props;
        const state = this.state;
        if (!state.selectedId) return;
        const fileName = await AppUtils.callService(() => eventService.getEventPrint(props.companyId, state.selectedId, printId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    getTimeSuffix = (date: number): string => {
        return !Base.isZeroTime(date) ? " " + Base.timeToTimeStr(date) : "";
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 7, display: "flex", overflow: "hidden" }}>
                    <PageTitle title={Translations.Events + (props.savedFilter ? " - " + props.savedFilter.title : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))} position="absolute">
                        <AddButton variant="contained" onClick={this.handleAdd}>
                            {Translations.AddEvent}
                        </AddButton>
                        <PreviewToggleButton value="preview" selected={props.showDocumentPreview} onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)} style={{ marginLeft: "24px", padding: "7px" }} />
                    </PageTitle>
                    <MainListContainer sx={{ width: "100%", overflow: "hidden" }}>
                        <ActionContainer
                            margin="1rem .5rem 1rem 1rem"
                            leftItems={
                                props.reportTabs
                            }
                            rightItems={
                                <>
                                    <ListActionButtons
                                        filterIsSet={props.listFilterIsSet}
                                        printDisabled={!state.selectedId}
                                        onFilter={props.onOpenFiltersDialog}
                                    />
                                    <ToolButtonDropdown
                                        variant="text"
                                        actions={state.userPrints.map((print) => ({
                                            divider: print.spacer,
                                            title: !print.spacer
                                                ? (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" key={print.id}>
                                                        <Typography variant="body2">{print.title}</Typography>
                                                        {print.id &&
                                                            <EditToolButton sx={{ margin: "0 -0.5rem 0 .5rem" }} onClick={(e) => this.handleShowPrintProperties(e, print.id)} />
                                                        }
                                                    </Box>
                                                )
                                                : null,
                                            onClick: () => print.id
                                                ? this.handlePrint(print.id)
                                                : this.handleAddPrint(),
                                        }))}
                                        title={Translations.Print}
                                        icon={<AppPrintIcon />}
                                        disabled={!state.selectedId}
                                        tooltip={Translations.AdditionalFunctions}
                                        menuRight={true}
                                        margin="0 .5rem 0 0"
                                    />
                                    <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                                </>
                            }
                        />
                        <ListSearchContainer>
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter} />
                        </ListSearchContainer>
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={state.hasMore}
                            globalLoadingInUse={!props.disableGlobalLoading}
                            height="calc(100vh - 296px)" //Main List
                            header={state.listModeIsLine
                                ? {
                                    columns: [
                                        { size: props.showDocumentPreview ? 2 : 1, column: "unitCode", content: Translations.Unit },
                                        !props.showDocumentPreview ? { size: 1, column: "", content: Translations.EventType } : undefined,
                                        { size: props.showDocumentPreview ? 3 : 2, column: "state", content: Translations.State },
                                        { size: props.showDocumentPreview ? 2 : 1, column: "date", content: Translations.Date },
                                        { size: props.showDocumentPreview ? 2 : 1, column: "UserName", content: Translations.Observer },
                                        !props.showDocumentPreview ? { size: 2, column: "", content: Translations.Handlers } : undefined,
                                        { size: props.showDocumentPreview ? 3 : 2, column: "name", content: Translations.Title },
                                        !props.showDocumentPreview ? { size: 2, column: "description", content: Translations.Description } : undefined
                                    ]
                                }
                                : null}
                            lines={state.listModeIsLine
                                ? state.items.map((item) => {
                                    return {
                                        columns: [
                                            {
                                                size: props.showDocumentPreview ? 2 : 1,
                                                content:
                                                    <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>
                                                        {item.unitCode}
                                                    </ListLinkValue>
                                            },
                                            !props.showDocumentPreview ? { size: 1, content: item.eventTypes } : undefined,
                                            { size: props.showDocumentPreview ? 3 : 2, content: <EventListStateCol item={item} /> },
                                            { size: props.showDocumentPreview ? 2 : 1, content: Base.utcTimeToDateStr(item.date), tooltip: Base.timeToDateStr(item.date) + this.getTimeSuffix(item.date) },
                                            { size: props.showDocumentPreview ? 2 : 1, content: item.observer },
                                            !props.showDocumentPreview ? { size: 2, content: <EventListHandlersCol handlers={item.handlers} /> } : undefined,
                                            { size: props.showDocumentPreview ? 3 : 2, content: item.name },
                                            !props.showDocumentPreview ? { size: 2, content: item.description } : undefined
                                        ],
                                        id: item.id
                                    };
                                })
                                : null
                            }
                            cards={!state.listModeIsLine
                                ? state.items.map((item) => {
                                    return {
                                        sm: 4,
                                        rows: [
                                            {
                                                cols: [
                                                    {
                                                        size: 6,
                                                        title: Translations.Code,
                                                        value:
                                                            <Box display="flex" justifyContent="space-between" flexGrow={2}>
                                                                <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>
                                                                    {item.code}
                                                                </ListLinkValue>
                                                            </Box>
                                                    },
                                                    { size: 6, title: Translations.Date, value: Base.timeToDateStr(item.date) + this.getTimeSuffix(item.date) }
                                                ]
                                            },
                                            {
                                                cols: [
                                                    { title: Translations.Title, value: item.name },
                                                ]
                                            },
                                            {
                                                cols: [
                                                    { title: Translations.Description, value: item.description },
                                                ]
                                            },
                                            {
                                                cols: [
                                                    { size: 6, title: Translations.State, value: <EventListStateCol item={item} /> },
                                                    { size: 6, title: Translations.Unit, value: item.unitCode }
                                                ]
                                            }],
                                        id: item.id
                                    };
                                })
                                : null
                            }
                            lineDetails={state.detailsItem && !props.showDocumentPreview
                                ? <Box position="relative">
                                    <GridRow>
                                        <Grid item sm={4}>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.Date} value={Base.timeToDateStr(state.detailsItem.date) + this.getTimeSuffix(state.detailsItem.date)} />
                                                </Grid>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.EventUrgencyClass} value={state.detailsItem.urgencyClassCode} />
                                                </Grid>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.Unit} value={state.detailsItem.unitCode} />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.Title} value={state.detailsItem.name} />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.Observer} value={state.detailsItem.observer} />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField keepLineBreaks title={Translations.Description} value={state.detailsItem.description} />
                                                </Grid>
                                            </GridRow>
                                        </Grid>
                                        <Grid item sm={4}>
                                            {state.detailsItem.observerActionInUse &&
                                                <GridRow>
                                                    <Grid item xs>
                                                        <ListCardField keepLineBreaks title={Translations.ObserverAction} value={state.detailsItem.observerAction} />
                                                    </Grid>
                                                </GridRow>
                                            }
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField keepLineBreaks title={Translations.ImmediateAction} value={state.detailsItem.immediateAction} />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField keepLineBreaks title={Translations.PreventiveAction} value={state.detailsItem.preventiveAction} />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField keepLineBreaks title={Translations.RootCause} value={state.detailsItem.rootCause} />
                                                </Grid>
                                            </GridRow>
                                        </Grid>
                                        <Grid item sm={4}>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField
                                                        title={Translations.Handlers}
                                                        value={
                                                            <EventListDetailsHandlersField
                                                                sessionState={props.sessionState}
                                                                item={state.detailsItem}
                                                                onApprove={this.handleApprove}
                                                            />
                                                        }
                                                    />
                                                </Grid>
                                            </GridRow>
                                            <GridRow>
                                                <Grid item xs>
                                                    <ListCardField title={Translations.AmountOfComments} value={state.detailsItem.messageCount.toString(10)} />
                                                </Grid>
                                            </GridRow>
                                        </Grid>
                                    </GridRow>
                                    <ListDetailsButton
                                        onClick={() => this.handleOpenLine(state.selectedId)}
                                    />
                                </Box>
                                : null}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenLine}
                        />
                    </MainListContainer>
                    {state.showNewEventDialog &&
                        <EventEditNewEventDialog
                            sessionState={props.sessionState}
                            companyId={props.companyId}
                            onOk={this.handleEditNewRecordDialogOk}
                            onCancel={this.handleEditNewRecordDialogCancel}
                        />
                    }
                    {(state.showEditDialog && !!state.editDialogId) &&
                        <EventEditDialog
                            sessionState={props.sessionState}
                            eventId={state.editDialogId}
                            companyId={props.companyId}
                            hasPrevious={state.editDialogHasPrevious}
                            hasNext={state.editDialogHasNext}
                            onOk={this.handleEventDialogOk}
                            onClose={this.handleEventDialogCancel}
                            onRemoved={() => this.setState({ selectedId: "" })}
                            onRefreshList={() => this.refreshList(false)}
                            onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                            onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        />
                    }
                    {state.showEventPrintDialog &&
                        <PrintOptionsDialog
                            print={state.eventPrintDialogPrint}
                            printableFields={state.eventPrintDialogPrintableFields}
                            applicationPermissionType={ApplicationPermissionType.Event}
                            reservedNames={state.eventPrintDialogReservedNames}
                            onPrintSaved={this.handlePrintDialogSaved}
                            onPrintRemoved={this.handlePrintDialogRemoved}
                            onCancel={this.hanlePrintDialogCancel}
                        />
                    }
                </div>
                {props.showDocumentPreview &&
                    <EventListDetailsPanel
                        sessionState={props.sessionState}
                        eventId={state.selectedId}
                        companyId={props.companyId}
                        dataId={props.dataId}
                        editDialogVisible={state.showEditDialog}
                        onRefreshList={() => this.refreshList(false)}
                        onClose={() => props.onChangeShowDocumentPreview(false)}
                    />
                }
            </>
        );
    }
}